import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  TextField,
  Avatar,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import CategoryService from '../../services/category.service';
import { usePagination } from '../../hooks/usePagination';

const CategoryList: React.FC = () => {
  const navigate = useNavigate();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(25);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: ['image', 'parentCategory', 'subCategories'],
        'pagination[page]': pagination.page + 1,
        'pagination[pageSize]': pagination.pageSize,
        sort: 'sortOrder:asc',
      };

      // Add search filter if search term exists
      if (searchTerm) {
        params['filters[name][$containsi]'] = searchTerm;
      }

      const response = await CategoryService.getAll(params);
      const categoriesData = response.data?.data || [];
      const paginationMeta = response.data?.meta?.pagination || {};
      
      setCategories(categoriesData);
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, searchTerm, setPagination]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        handlePageChange(0);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, handlePageChange]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await CategoryService.delete(id);
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category. It may have products assigned to it.');
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Reset to first page when search changes
    setPagination({ page: 0 });
  };

  return (
      <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Product Categories
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/categories/create')}
        >
          Add Category
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          label="Search categories..."
          variant="outlined"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Parent Category</TableCell>
              <TableCell>Sub-Categories</TableCell>
              <TableCell>Sort Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No categories found
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category) => {
                const attrs = category.attributes;
                const imageUrl = attrs?.image?.data?.attributes?.url
                  ? `${(process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '')}${attrs.image.data.attributes.url}`
                  : null;

                return (
                  <TableRow key={category.id} hover>
                    <TableCell>
                      <Avatar
                        src={imageUrl || undefined}
                        alt={attrs?.name}
                        variant="rounded"
                      >
                        <CategoryIcon />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {attrs?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {attrs?.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                        {attrs?.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {attrs?.parentCategory?.data ? (
                        <Chip
                          label={attrs.parentCategory.data.attributes?.name}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {attrs?.subCategories?.data?.length > 0 ? (
                        <Chip
                          label={`${attrs.subCategories.data.length} sub-categories`}
                          size="small"
                          color="primary"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={attrs?.sortOrder || 0} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.isActive ? 'Active' : 'Inactive'}
                        color={attrs?.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/categories/${category.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/categories/${category.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(category.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.pageSize}
          page={pagination.page}
          onPageChange={(_, newPage) => handlePageChange(newPage)}
          onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
        />
      )}
      </Box>
  );
};

export default CategoryList;

